import { DRAWERS, DRAWER_LABELS, whoSeesDrawer } from '@/lib/api'
import { UNFILED_VALUE, drawerChoiceValue, parseDrawerChoice, type DrawerChoice } from '@/lib/drawer-choice'

/**
 * WHICH DRAWER? (Kelvo item 2) — the one control every filing screen uses:
 * upload, paste, answering a question, and moving a kept file.
 *
 * ONE COMPONENT, SO ONE SET OF WORDS. Each option names the drawer as it is
 * printed on the card AND who it reaches ("03 Safety — You, the office and
 * site staff"), read from the same role table the Worker enforces. The owner
 * decides who is told something at the moment he files it, so that is the
 * moment the consequence is shown.
 *
 * NO DEFAULT. The first option is "Choose a drawer…" with an empty value, and
 * every filing screen refuses to submit until a real drawer is chosen. A
 * pre-selected drawer would be a guess, made for him, about who should see his
 * paperwork.
 *
 * `allowUnfiled` adds "Not in a drawer — Only you" for the move control, where
 * taking a file back out of every drawer is a real choice. The filing screens
 * leave it out: something new is filed, not left loose.
 */
export default function DrawerPicker({
  id,
  label,
  hint,
  value,
  onChange,
  disabled = false,
  allowUnfiled = false,
}: {
  id: string
  label: string
  hint?: string
  value: DrawerChoice
  onChange: (next: DrawerChoice) => void
  disabled?: boolean
  allowUnfiled?: boolean
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-text-primary">
        {label}
      </label>
      {hint ? <p className="mt-0.5 text-xs text-text-muted">{hint}</p> : null}
      <select
        id={id}
        value={drawerChoiceValue(value)}
        onChange={(e) => onChange(parseDrawerChoice(e.target.value))}
        disabled={disabled}
        className="mt-2 w-full rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
      >
        <option value="" disabled>
          Choose a drawer…
        </option>
        {DRAWERS.map((d) => (
          <option key={d} value={d}>
            {DRAWER_LABELS[d]} — {whoSeesDrawer(d)}
          </option>
        ))}
        {allowUnfiled ? <option value={UNFILED_VALUE}>Not in a drawer — {whoSeesDrawer(null)}</option> : null}
      </select>
    </div>
  )
}
